import React from "react";
import analytics from "web/script/analytics/analytics";
import environment from "web/script/modules/environment";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import styles from "./size-options-form.css";

export interface Size {
    raw_size: string;
    human_size: string;
    min_price: string;
    option_value: string;
}

export interface SizeOptionsFormProps {
    action: string;
    csrfToken: string;
    country: string;
    returnUrl: string;
    availableSizes: number;
    sizes: Size[];
    storesTip: string;
    onSizeChange: (Size) => void;
}

export function SizeOptionsForm({
    action,
    csrfToken,
    country,
    returnUrl,
    availableSizes,
    sizes,
    storesTip,
    onSizeChange,
}: SizeOptionsFormProps): React.ReactElement {
    const [sizePickerError, setSizePickerError] = React.useState(false);
    const [searchingStores, setSearchingStores] = React.useState(false);
    const [renderStoresOptions, setRenderStoresOptions] = React.useState(false);
    const buyNowVariant = environment.getFeature("checkout_buy_now");

    function handleSubmit(e: React.FormEvent<HTMLFormElement>): void {
        e.preventDefault();
        const { buyOption } = e.currentTarget.elements as any;

        if (!buyOption.value) {
            setSizePickerError(true);
            analytics.event("buy_area", "pick_a_size_error");

            return;
        }

        e.currentTarget.submit();
    }

    function handleSelect(event): void {
        setSizePickerError(false);
        setSearchingStores(true);

        onSizeChange(JSON.parse(event.target.value));

        if (!renderStoresOptions) {
            setTimeout(() => {
                setRenderStoresOptions(true);
            }, 1400);
        }
    }

    function handleFocus(): void {
        analytics.event("buy_area", "size_picker_focus", String(availableSizes));
    }

    React.useEffect(() => {
        if (availableSizes === 1) {
            onSizeChange(sizes[0].option_value);
            setSearchingStores(true);

            setTimeout(() => {
                setRenderStoresOptions(true);
            }, 600);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableSizes]);

    return (
        <div>
            <form
                className={`${styles.form} mt10`}
                action={action}
                method="post"
                autoComplete="off"
                onSubmit={handleSubmit}
            >
                <input type="hidden" name="csrfmiddlewaretoken" value={csrfToken} />
                <input type="hidden" name="country" value={country} />
                <input type="hidden" name="return_url" value={returnUrl} />

                <div
                    className={`${styles.selectWrapper} ${
                        sizePickerError ? styles.selectWrapperError : ""
                    } select-dropdown`}
                >
                    <select
                        name="buyOption"
                        className="select-dropdown__input"
                        aria-required="true"
                        defaultValue={
                            availableSizes > 1 ? "" : JSON.stringify(sizes[0].option_value)
                        }
                        onChange={handleSelect}
                        onFocus={handleFocus}
                    >
                        {availableSizes > 1 && (
                            <option value="" disabled>
                                {"pick a size"}
                                {/* todo I18N-291 */}
                            </option>
                        )}
                        {sizes.map((size: Size) => {
                            const value = JSON.stringify(size.option_value);
                            const shouldDisplayRetailerRawSize =
                                environment.getFeature("checkp_raw_sizes") &&
                                size.human_size !== size.raw_size;

                            return (
                                <option
                                    key={value}
                                    value={value}
                                    data-human-size={size.human_size}
                                    data-raw-size={size.raw_size}
                                >
                                    {shouldDisplayRetailerRawSize
                                        ? `${size.human_size} / ${size.raw_size}`
                                        : size.human_size}
                                </option>
                            );
                        })}
                    </select>

                    {sizePickerError ? (
                        <div className="form-item-error">
                            <div>{"Please pick a size"}</div>
                        </div>
                    ) : null}
                </div>

                <div className={styles.buttonWrapper}>
                    {!renderStoresOptions ? (
                        <button
                            type="submit"
                            className={`${
                                sizePickerError ? styles.buttonErrorState : ""
                            } btn btn-cta btn--multiply btn--full-width buybuybuy-area__size-options__select-size-button`} // keeping buybuybuy-area className to register sticky button, safe to remove afterwards
                            disabled={searchingStores}
                        >
                            <div className="btn--multiply-label btn--multiply-label--top">
                                {!buyNowVariant && <SVGIcon name="plus-bag" />}
                                <span>
                                    {buyNowVariant ? "Buy now" : "Add to bag"}
                                    {/* todo I18N-291 */}
                                </span>
                            </div>
                            <div className="btn--multiply-label btn--multiply-label--bottom">
                                {!buyNowVariant && <SVGIcon name="plus-bag" />}
                                <span>
                                    {buyNowVariant ? "Buy now" : "Add to bag"}
                                    {/* todo I18N-291 */}
                                </span>
                            </div>
                            <div className="btn--multiply-label btn--multiply-label--center">
                                {!buyNowVariant && <SVGIcon name="plus-bag" />}
                                <span>
                                    {buyNowVariant ? "Buy now" : "Add to bag"}
                                    {/* todo I18N-291 */}
                                </span>
                            </div>
                        </button>
                    ) : (
                        <p className={`${styles.storesCountText} ml10`}>{storesTip}</p>
                    )}
                </div>
            </form>
        </div>
    );
}

export default SizeOptionsForm;
